import React, { PureComponent } from 'react';
import { arrayOf, bool, func, object, string } from 'prop-types';
import { paymentMethodType, skuType, stripeObjectType } from 'types';
import { H2 } from '@sohonet/ui-components/Typography';

import { Field } from 'formik';
import { components } from 'react-select';

import { injectStripe } from 'react-stripe-elements';
import {
  MdKeyboardArrowRight,
  MdFiberManualRecord as Circle
} from 'react-icons/md';

import { renderPaymentMethodLogo } from 'components/PaymentMethod';

import Button, { GhostButton } from 'ui/Button';
import Form, {
  FormBody,
  FormFooter,
  FormSection,
  FormCreditInfoSection
} from 'ui/Form';
import Input from 'ui/Input';
import FormSteps from 'components/FormSteps';
import InputWrapper from 'components/InputWrapper';
import CreditDetails from 'components/CreditDetails';
import LoadingIndicator from 'ui/LoadingIndicator';
import Message from 'components/Message';
import Select from 'ui/Select';
import StripeLink from 'components/StripeLink';

import { formatMonthYear } from 'utils/dates';
import formatCurrency from 'utils/formatCurrency';
import { FORM_STATUSES, LOCALES } from 'utils/constants';

import * as styles from './styles';

class InnerForm extends PureComponent {
  state = {
    page: 0,
    pages: 3
  };

  CreditOption = (props) => (
    <components.Option {...props}>
      {this.renderCreditOption(props.data)}
    </components.Option>
  );

  CreditSelectedValue = (props) => (
    <components.SingleValue {...props}>
      {this.renderCreditOption(props.data)}
    </components.SingleValue>
  );

  PaymentMethodOption = (props) => (
    <components.Option {...props}>
      {this.renderPaymentMethodOption(props.data)}
    </components.Option>
  );

  PaymentMethodSelectedValue = (props) => (
    <components.SingleValue {...props}>
      {this.renderPaymentMethodOption(props.data)}
    </components.SingleValue>
  );

  getElementError = (element) =>
    this.props.touched[element] ? this.props.errors[element] : null;

  renderErrorMessage = () =>
    this.props.errors.description && (
      <Message type={Message.types.ERROR} withMarginBottom>
        {this.props.errors.description}
      </Message>
    );

  next = () => {
    this.setState((state) => ({
      page: Math.min(state.page + 1, state.pages - 1)
    }));
  };

  onSubmit = (e) => {
    e.preventDefault();
    const { page } = this.state;
    const {
      handleSubmit,
      onSubmit,
      resetForm,
      setErrors,
      setFieldTouched,
      setFieldValue,
      setStatus,
      setSubmitting,
      stripe,
      values
    } = this.props;

    const actions = {
      resetForm,
      setErrors,
      setFieldTouched,
      setFieldValue,
      setStatus,
      setSubmitting
    };

    // If we're on the first page, we don't want to call the
    // `onSubmit` handler, but just move to the next step
    if (page !== 0) {
      handleSubmit(e);
      onSubmit(values, actions, stripe);
    }

    // We only proceed to the next step if the values are present
    if (values.skuAmount && values.skuId && values.paymentMethodId) {
      this.next();
    }
  };

  // todo refactor to a separate component
  // eslint-disable-next-line class-methods-use-this
  renderCreditOption = (option) => (
    <styles.OptionItem>
      <span>{option.name}</span>
      <span>
        {formatCurrency(
          LOCALES[option.currency.toUpperCase()],
          option.currency,
          option.price / 100
        )}
      </span>
    </styles.OptionItem>
  );

  // todo refactor to a separate component
  // eslint-disable-next-line class-methods-use-this
  renderPaymentMethodOption = (card) => (
    <styles.CardOptionItem>
      <span>{renderPaymentMethodLogo(card.brand, 40)}</span>
      <styles.Last4Wrapper>
        <span>
          <Circle />
          <Circle />
          <Circle />
          <Circle />
        </span>
        <span>{card.last4}</span>
      </styles.Last4Wrapper>
      <span>{card.name}</span>
      <span>{formatMonthYear(card.exp_month, card.exp_year)}</span>
    </styles.CardOptionItem>
  );

  renderCreditTypePage = () => {
    const { errors, setFieldTouched, setFieldValue, skus, touched, values } =
      this.props;

    const SKU_ID = 'skuId';
    const skuIdError = this.getElementError(SKU_ID);
    const skuIdLabel = 'Credit type';
    const selectedSku = skus.find(({ sku_id }) => sku_id === values[SKU_ID]);

    const SKU_AMOUNT = 'skuAmount';
    const skuAmountError = this.getElementError(SKU_AMOUNT);
    const skuAmountLabel = 'Quantity';

    return (
      <FormSection gridTemplateCols="1fr 60px" noMarginBottom>
        <InputWrapper error={skuIdError} label={skuIdLabel} name={SKU_ID}>
          <Select
            config={{
              components: {
                Option: this.CreditOption,
                SingleValue: this.CreditSelectedValue
              },
              getOptionLabel: ({ name }) => name,
              getOptionValue: ({ sku_id }) => sku_id
            }}
            haserror={!!errors[SKU_ID]}
            id={SKU_ID}
            key="skuSelect"
            name={SKU_ID}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={skus}
            touched={touched[SKU_ID]}
            value={selectedSku}
          />
        </InputWrapper>
        <InputWrapper
          error={skuAmountError}
          label={skuAmountLabel}
          name={SKU_AMOUNT}
        >
          <Input
            as={Field}
            data-testid={SKU_AMOUNT}
            haserror={skuAmountError}
            id={SKU_AMOUNT}
            min="1"
            name={SKU_AMOUNT}
            value={values.skuAmount}
            step="1"
            type="number"
          />
        </InputWrapper>
      </FormSection>
    );
  };

  renderPaymentMethodPage = () => {
    const {
      errors,
      setFieldTouched,
      setFieldValue,
      paymentMethods,
      touched,
      values
    } = this.props;

    const PAYMENT_METHOD_ID = 'paymentMethodId';
    const paymentMethodIdError = this.getElementError(PAYMENT_METHOD_ID);
    const paymentMethodIdLabel = 'Payment method';
    const selectedPaymentMethod = paymentMethods.find(
      ({ id }) => id === values[PAYMENT_METHOD_ID]
    );

    return (
      <FormSection noMarginBottom>
        <InputWrapper
          error={paymentMethodIdError}
          fullWidth
          label={paymentMethodIdLabel}
          name={PAYMENT_METHOD_ID}
        >
          <Select
            config={{
              components: {
                Option: this.PaymentMethodOption,
                SingleValue: this.PaymentMethodSelectedValue
              },
              getOptionLabel: ({ name }) => name,
              getOptionValue: ({ id }) => id
            }}
            haserror={!!errors[PAYMENT_METHOD_ID]}
            id={PAYMENT_METHOD_ID}
            key="paymentMethodSelect"
            name={PAYMENT_METHOD_ID}
            onBlur={setFieldTouched}
            onChange={setFieldValue}
            options={paymentMethods}
            touched={touched[PAYMENT_METHOD_ID]}
            value={selectedPaymentMethod}
          />
        </InputWrapper>
      </FormSection>
    );
  };

  renderOrderSummary = () => {
    const { orderData } = this.props.values;
    if (!orderData) {
      return null;
    }

    const { order } = orderData;
    const { amount, currency, skus, tax_amount } = order;

    const orderItems = skus.map((sku, i) => {
      const { description, quantity } = sku;
      return (
        // eslint-disable-next-line react/no-array-index-key
        <li key={`order-sku-${i}`}>
          <span>
            {description} {`x${quantity}`}
          </span>
          <span>
            {formatCurrency(
              sku.currency && LOCALES[sku.currency.toUpperCase()],
              sku.currency,
              (quantity * sku.amount) / 100
            )}
          </span>
        </li>
      );
    });

    const orderTax = tax_amount ? (
      <li>
        <span>Sales tax</span>
        <span>
          {formatCurrency(
            currency && LOCALES[currency.toUpperCase()],
            currency,
            tax_amount / 100
          )}
        </span>
      </li>
    ) : (
      ''
    );

    const orderTotal = (
      <li>
        <span>Total</span>
        <span>
          {formatCurrency(
            currency && LOCALES[currency.toUpperCase()],
            currency,
            amount / 100
          )}
        </span>
      </li>
    );

    return (
      <styles.OrderSummary noMarginBottom>
        <div>
          <H2>Order Summary</H2>
          <ul>
            {orderItems}
            {orderTax}
            {orderTotal}
          </ul>
        </div>
      </styles.OrderSummary>
    );
  };

  renderButtons = (isLastPage) => {
    const { hideModal, isSubmitting, status, values } = this.props;
    return (
      <FormSection noMarginBottom>
        <GhostButton
          disabled={isSubmitting}
          name="cancelBtn"
          onClick={hideModal}
          type="button"
        >
          Cancel
        </GhostButton>
        <Button
          disabled={
            isSubmitting ||
            status === FORM_STATUSES.UNRECOVERABLE ||
            (isLastPage && !values.orderData)
          }
          name="submitBtn"
          type="submit"
        >
          {isLastPage ? (
            'Buy now'
          ) : (
            <>
              Next <MdKeyboardArrowRight />
            </>
          )}
        </Button>
      </FormSection>
    );
  };

  renderCreditDetails = () => {
    const { skus, values } = this.props;

    const SKU_ID = 'skuId';
    const selectedSku = skus.find(({ sku_id }) => sku_id === values[SKU_ID]);
    return <CreditDetails sku={selectedSku} />;
  };

  render() {
    const { isSubmitting } = this.props;
    const { page } = this.state;

    const pages = [
      this.renderCreditTypePage(),
      this.renderPaymentMethodPage(),
      this.renderOrderSummary()
    ];
    const activePage = pages[page];
    const isLastPage = page === pages.length - 1;
    const isFirstPage = page === 0;

    const steps = [
      { name: 'Select credit' },
      { name: 'Payment method' },
      { name: 'Confirm order' }
    ];

    return (
      <Form onSubmit={(e) => this.onSubmit(e)}>
        {isSubmitting && <LoadingIndicator cover />}
        <FormBody>
          <FormSteps steps={steps} activeIndex={page} />
          {this.renderErrorMessage()}
          {activePage}
          <FormCreditInfoSection>
            {isFirstPage ? this.renderCreditDetails() : <div />}
            <StripeLink />
          </FormCreditInfoSection>
        </FormBody>
        <FormFooter>{this.renderButtons(isLastPage)}</FormFooter>
      </Form>
    );
  }
}

InnerForm.propTypes = {
  errors: object,
  handleSubmit: func,
  hideModal: func.isRequired,
  isSubmitting: bool,
  onSubmit: func,
  paymentMethods: arrayOf(paymentMethodType).isRequired,
  resetForm: func,
  setErrors: func,
  setFieldTouched: func,
  setFieldValue: func,
  setStatus: func,
  setSubmitting: func,
  skus: arrayOf(skuType).isRequired,
  status: string,
  stripe: stripeObjectType.isRequired,
  touched: object,
  values: object
};

export default injectStripe(InnerForm);
